/* eslint-disable jsx-a11y/accessible-emoji */
import React from 'react';
import PropTypes from 'prop-types';
import MomentUtils from '@date-io/moment';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { withStyles, Typography, Button } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import moment from 'moment';
import ContentHeader from './ContentHeader';
import {
    calc,
    isUnavailableFn,
    isAnyAvailable,
    isDateAvailable
} from '../helpers/service-availability';
import { getCurrencySymbol, getYearGroups } from '../helpers/ticket';
import globalStyles from '../styles';

const styles = () => ({
    root: {
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)'
    },
    form: {
        padding: '0 10px 10px'
    },
    description: {
        padding: '20px 0 0',
        fontWeight: '100',
        textAlign: 'center'
    },
    name: {
        margin: '0 auto 20px'
    },
    input: {
        width: '100%',
        maxWidth: '250px',
        display: 'block',
        margin: '20px auto',
        '& > div': {
            display: 'block'
        },
        '& input': {
            width: 220
        }
    },
    button: {
        height: '56px',
        margin: '0 auto 10px'
    },
    uploadButton: {
        height: '56px',
        margin: '0 auto',
        padding: '0.6em'
    },
    confirmButton: {
        height: '56px',
        padding: '0.6em'
    },
    upload: {
        display: 'none'
    },
    imageUpload: {
        margin: '0 0 40px 0',
        textAlign: 'center'
    },
    datePicker: {
        margin: '0 auto 20px',
        maxWidth: '250px',
        '& > div': {
            width: '100%'
        }
    },
    formControl: {
        maxWidth: 250,
        width: '100%',
        margin: '0 auto 20px',
        display: 'flex'
    },
    smallPrint: {
        fontSize: 13,
        textAlign: 'center',
        color: '#444',
        marginBottom: 10
    },
    confirm: {
        display: 'flex'
    },
    zoneMenuItem: {
        whiteSpace: 'normal'
    },
    link: globalStyles.link
});

const validateForm = (
    passenger,
    service,
    ticket,
    serviceAvailability,
    useZones
) => {
    let stopSelectionValid = true;
    let dateValid = true;
    let timeValid = true;
    let availabilityValid = true;
    let yearGroupIsValid = true;
    let destinationValid = true;
    let zoneSelectionValid = true;

    if (service && 'stops' in service && !useZones) {
        stopSelectionValid = service.stops.includes(passenger.stop);
    }

    if (useZones) {
        const zone =
            passenger.zone &&
            service.zones.find(
                serviceZone => serviceZone.name === passenger.zone
            );

        if (zone && (!zone.stops || !zone.stops.length)) {
            zoneSelectionValid = true;
        } else if (
            zone &&
            zone.stops &&
            zone.stops.includes(passenger.zoneStop)
        ) {
            zoneSelectionValid = true;
        } else {
            zoneSelectionValid = false;
        }
    }

    if (service && service.destinations && service.destinations.length) {
        destinationValid = service.destinations.includes(passenger.dest);
    }

    if (ticket && ticket.type === 'single') {
        dateValid = passenger.startDate;
        timeValid =
            passenger.startTime === 'am' || passenger.startTime === 'pm';
        if (dateValid && timeValid) {
            availabilityValid = isDateAvailable(
                ticket,
                serviceAvailability,
                passenger.startDate,
                passenger.startTime
            );
        }
    }

    if (service && 'yr_groups' in service) {
        const yearGroups = getYearGroups(service.yr_groups);

        if (yearGroups.length) {
            yearGroupIsValid = yearGroups.includes(passenger.yr);
        }
    }

    if (
        ticket &&
        (ticket.type === 'return' ||
            ticket.type === '4_week' ||
            ticket.type === 'week')
    ) {
        dateValid = passenger.startDate;
        if (dateValid) {
            availabilityValid = isDateAvailable(
                ticket,
                serviceAvailability,
                passenger.startDate,
                passenger.startTime
            );
        }
    }

    return (
        stopSelectionValid &&
        destinationValid &&
        passenger.firstName &&
        passenger.lastName &&
        dateValid &&
        timeValid &&
        availabilityValid &&
        yearGroupIsValid &&
        zoneSelectionValid
    );
};

const availablilityStatement = availability => {
    if (availability === null) {
        return <></>;
    }

    if (availability <= 0) {
        return (
            <p
                style={{
                    textAlign: 'center',
                    color: 'red'
                }}
            >
                There are no tickets remaining on this date.
            </p>
        );
    }

    if (availability < 10) {
        return (
            <p
                style={{
                    textAlign: 'center',
                    color: 'red'
                }}
            >
                Hurry, only {availability} tickets available!
            </p>
        );
    }

    if (availability > 0) {
        return (
            <p
                style={{
                    textAlign: 'center',
                    color: 'green'
                }}
            >
                Tickets available.
            </p>
        );
    }

    return <></>;
};

const renderZonePricing = ({ serviceZone, ticket }) => {
    const price = ticket.zone_prices[serviceZone.name];

    if (ticket.payment_type === 'dd') {
        const monthly = `${
            ticket.num_monthly_payments
        } monthly payments x ${getCurrencySymbol(ticket.currency)}${
            ticket.zone_prices_monthly[serviceZone.name]
        }`;
        const deposit = `${getCurrencySymbol(ticket.currency)}${
            ticket.zone_prices_deposit[serviceZone.name]
        }`;
        return `${getCurrencySymbol(
            ticket.currency
        )}${price} (${deposit} + ${monthly})`;
    }

    return `${getCurrencySymbol(ticket.currency)}${price}`;
};

const renderZoneOption = ({ classes, serviceZone, ticket }) => (
    <MenuItem
        className={classes.zoneMenuItem}
        key={`zone-${serviceZone.name}`}
        value={serviceZone.name}
    >
        {serviceZone.name} - {renderZonePricing({ serviceZone, ticket })}
    </MenuItem>
);

const zoneHasPrice = (serviceZone, ticket) => {
    return (
        serviceZone.name in ticket.zone_prices &&
        ticket.zone_prices[serviceZone.name] > 0
    );
};

const hasZoneStops = (passenger, service) => {
    const zone =
        passenger.zone &&
        service.zones.find(serviceZone => serviceZone.name === passenger.zone);
    return zone && zone.stops && zone.stops.length > 0;
};

const renderZonesForm = ({
    classes,
    passenger,
    service,
    ticket,
    handleChange
}) => (
    <>
        <FormControl
            variant="outlined"
            className={classes.formControl}
            style={{ marginTop: 20 }}
        >
            <InputLabel style={{ background: 'white' }}>Zone</InputLabel>
            <Select
                id="zone"
                name="zone"
                value={passenger.zone}
                onChange={handleChange}
                variant="outlined"
                input={
                    <OutlinedInput
                        name="zone"
                        labelWidth={0}
                        id="outlined-zone"
                    />
                }
            >
                {service.zones
                    .filter(serviceZone => zoneHasPrice(serviceZone, ticket))
                    .map(serviceZone =>
                        renderZoneOption({ classes, serviceZone, ticket })
                    )}
            </Select>
        </FormControl>
        {hasZoneStops(passenger, service) && (
            <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel style={{ background: 'white' }}>
                    Bus stop
                </InputLabel>
                <Select
                    id="zone-stop"
                    name="zoneStop"
                    value={passenger.zoneStop}
                    onChange={handleChange}
                    variant="outlined"
                    disabled={!passenger.zone}
                    input={
                        <OutlinedInput
                            name="zoneStop"
                            labelWidth={0}
                            id="outlined-zone-stop"
                        />
                    }
                >
                    {passenger.zone &&
                        service.zones
                            .find(
                                serviceZone =>
                                    serviceZone.name === passenger.zone
                            )
                            .stops.map(zoneStop => (
                                <MenuItem
                                    key={`zone-stop-${zoneStop}`}
                                    value={zoneStop}
                                >
                                    {zoneStop}
                                </MenuItem>
                            ))}
                </Select>
            </FormControl>
        )}
    </>
);

const BuyAgainForm = ({
    handleChange,
    handleDateChange,
    handleSubmit,
    passenger,
    service,
    serviceAvailability,
    ticket,
    classes,
    requireStudentId
}) => {
    const shouldDisableDateFn = isUnavailableFn(
        ticket,
        serviceAvailability,
        passenger
    );
    const availability = calc(
        ticket,
        serviceAvailability,
        passenger.startDate,
        passenger.startTime
    );

    let yearGroups = [];

    if (service) {
        yearGroups = getYearGroups(service.yr_groups);
    }

    const useZones =
        service.zones && ticket.pricing_model === 'Zonal' && ticket.zone_prices;

    const showDestination =
        service && service.destinations && service.destinations.length > 0;

    const availablilityEl = availablilityStatement(availability);
    const isValid = validateForm(
        passenger,
        service,
        ticket,
        serviceAvailability,
        useZones
    );

    if (!isAnyAvailable(ticket, serviceAvailability, moment())) {
        return (
            <div className={classes.root}>
                <ContentHeader text="No availability" id="passenger-heading" />
                <Typography
                    style={{
                        padding: '0 10px',
                        textAlign: 'center'
                    }}
                    variant="body2"
                >
                    This type of ticket is currently unavailable.
                </Typography>
                <Typography
                    style={{
                        padding: '0 10px 20px',
                        textAlign: 'center'
                    }}
                    variant="body2"
                >
                    Please check again soon or contact your transport operator
                    directly for more information.
                </Typography>
            </div>
        );
    }

    const adhocTypes = ['single', 'return', 'week', '4_week'];
    const isAdhoc = ticket && adhocTypes.includes(ticket.type);

    return (
        <form onSubmit={handleSubmit}>
            {isAdhoc && (
                <MuiPickersUtilsProvider utils={MomentUtils}>
                    <div className={classes.root} style={{ marginBottom: 30 }}>
                        <ContentHeader
                            text="Check availability"
                            id="availability-heading"
                        />
                        <div className={classes.form}>
                            {ticket && ticket.type === 'single' && (
                                <>
                                    <FormControl
                                        variant="outlined"
                                        className={classes.formControl}
                                        style={{ marginTop: 20 }}
                                    >
                                        <InputLabel
                                            style={{ background: 'white' }}
                                        >
                                            Journey time
                                        </InputLabel>
                                        <Select
                                            id="startTime"
                                            name="startTime"
                                            value={passenger.startTime}
                                            onChange={handleChange}
                                            variant="outlined"
                                            input={
                                                <OutlinedInput
                                                    name="startTime"
                                                    labelWidth={0}
                                                    id="outlined-start-time"
                                                />
                                            }
                                        >
                                            <MenuItem key="start-am" value="am">
                                                AM - morning
                                            </MenuItem>
                                            <MenuItem key="start-pm" value="pm">
                                                PM - afternoon
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                    <div className={classes.datePicker}>
                                        <DatePicker
                                            autoOk
                                            value={passenger.startDate}
                                            onChange={handleDateChange}
                                            minDate={new Date()}
                                            inputVariant="outlined"
                                            label="Journey date"
                                            format="ddd Do MMM YYYY"
                                            maxDate={moment().add(7, 'days')}
                                            shouldDisableDate={
                                                shouldDisableDateFn
                                            }
                                        />
                                    </div>
                                    {availablilityEl}
                                    <Typography
                                        className={classes.smallPrint}
                                        variant="body2"
                                    >
                                        <strong>NOTE:</strong> Single tickets
                                        can only be purchased up to one week in
                                        advance.
                                    </Typography>
                                </>
                            )}
                            {ticket && ticket.type === 'return' && (
                                <>
                                    <div
                                        style={{ marginTop: 20 }}
                                        className={classes.datePicker}
                                    >
                                        <DatePicker
                                            autoOk
                                            value={passenger.startDate}
                                            onChange={handleDateChange}
                                            minDate={new Date()}
                                            inputVariant="outlined"
                                            label="Return journey date"
                                            format="ddd Do MMM YYYY"
                                            maxDate={moment().add(7, 'days')}
                                            shouldDisableDate={
                                                shouldDisableDateFn
                                            }
                                        />
                                    </div>
                                    {availablilityEl}
                                    <Typography
                                        className={classes.smallPrint}
                                        variant="body2"
                                    >
                                        <strong>NOTE:</strong> Return tickets
                                        can only be purchased up to one week in
                                        advance.
                                    </Typography>
                                </>
                            )}
                            {ticket && ticket.type === 'week' && (
                                <>
                                    <div
                                        style={{ marginTop: 20 }}
                                        className={classes.datePicker}
                                    >
                                        <DatePicker
                                            autoOk
                                            value={passenger.startDate}
                                            onChange={handleDateChange}
                                            minDate={new Date()}
                                            maxDate={moment().add(7, 'days')}
                                            inputVariant="outlined"
                                            label="Ticket start date"
                                            format="ddd Do MMM YYYY"
                                            shouldDisableDate={
                                                shouldDisableDateFn
                                            }
                                        />
                                    </div>
                                    {availablilityEl}
                                    <Typography
                                        className={classes.smallPrint}
                                        variant="body2"
                                    >
                                        <strong>NOTE:</strong> Week tickets can
                                        only be purchased up to one week in
                                        advance.
                                    </Typography>
                                </>
                            )}
                            {ticket && ticket.type === '4_week' && (
                                <>
                                    <div
                                        style={{ marginTop: 20 }}
                                        className={classes.datePicker}
                                    >
                                        <DatePicker
                                            autoOk
                                            value={passenger.startDate}
                                            onChange={handleDateChange}
                                            minDate={new Date()}
                                            maxDate={moment().add(7, 'days')}
                                            inputVariant="outlined"
                                            label="Ticket start date"
                                            format="ddd Do MMM YYYY"
                                            shouldDisableDate={
                                                shouldDisableDateFn
                                            }
                                        />
                                    </div>
                                    {availablilityEl}
                                    <Typography
                                        className={classes.smallPrint}
                                        variant="body2"
                                    >
                                        <strong>NOTE:</strong> Four week tickets
                                        can only be purchased up to one week in
                                        advance.
                                    </Typography>
                                </>
                            )}
                        </div>
                    </div>
                </MuiPickersUtilsProvider>
            )}
            {useZones && (
                <div className={classes.root} style={{ marginBottom: 30 }}>
                    <ContentHeader
                        text="Zone selection"
                        id="availability-heading"
                    />
                    <Typography className={classes.description} variant="body2">
                        Select your travel zone.{' '}
                        {service.timetable_url && (
                            <>
                                <a
                                    href={service.timetable_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={classes.link}
                                >
                                    Help finding your zone
                                </a>
                                .
                            </>
                        )}
                    </Typography>
                    <div className={classes.form}>
                        {renderZonesForm({
                            classes,
                            passenger,
                            service,
                            ticket,
                            handleChange
                        })}
                    </div>
                </div>
            )}
            <div className={classes.root}>
                <ContentHeader text="Passenger" id="passenger-heading" />
                <Typography className={classes.description} variant="body2">
                    Please confirm the details of the passenger this ticket is
                    for:
                </Typography>
                <div className={classes.form}>
                    <div className={classes.name}>
                        <TextField
                            autoFocus
                            autoComplete="off"
                            className={`${classes.input} input`}
                            id="firstName"
                            name="firstName"
                            type="text"
                            label="First name"
                            value={passenger.firstName}
                            onChange={handleChange}
                            disabled
                            variant="outlined"
                        />
                        <TextField
                            autoComplete="off"
                            className={`${classes.input} input`}
                            id="lastName"
                            name="lastName"
                            type="text"
                            label="Last name"
                            value={passenger.lastName}
                            onChange={handleChange}
                            disabled
                            variant="outlined"
                        />
                        {requireStudentId && (
                            <TextField
                                autoComplete="off"
                                className={`${classes.input} input`}
                                id="id"
                                name="id"
                                type="text"
                                label="Student ID"
                                value={passenger.id}
                                onChange={handleChange}
                                disabled
                                variant="outlined"
                            />
                        )}
                    </div>
                    {service && 'stops' in service && !useZones && (
                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                        >
                            <InputLabel style={{ background: 'white' }}>
                                Bus stop
                            </InputLabel>
                            <Select
                                id="stop"
                                name="stop"
                                value={passenger.stop}
                                onChange={handleChange}
                                variant="outlined"
                                input={
                                    <OutlinedInput
                                        name="stop"
                                        labelWidth={0}
                                        id="outlined-stop"
                                    />
                                }
                            >
                                {service.stops.map(serviceStop => (
                                    <MenuItem
                                        key={`stop-${serviceStop}`}
                                        value={serviceStop}
                                    >
                                        {serviceStop}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {showDestination && (
                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                        >
                            <InputLabel style={{ background: 'white' }}>
                                Destination
                            </InputLabel>
                            <Select
                                id="dest"
                                name="dest"
                                value={passenger.dest}
                                onChange={handleChange}
                                variant="outlined"
                                input={
                                    <OutlinedInput
                                        name="dest"
                                        labelWidth={0}
                                        id="outlined-dest"
                                    />
                                }
                            >
                                {service.destinations.map(dest => (
                                    <MenuItem key={`dest-${dest}`} value={dest}>
                                        {dest}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {yearGroups.length > 0 && (
                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                        >
                            <InputLabel style={{ background: 'white' }}>
                                Year group
                            </InputLabel>
                            <Select
                                id="yr"
                                name="yr"
                                value={passenger.yr}
                                onChange={handleChange}
                                variant="outlined"
                                input={
                                    <OutlinedInput
                                        name="yr"
                                        labelWidth={0}
                                        id="outlined-yr"
                                    />
                                }
                            >
                                {yearGroups.map(year => (
                                    <MenuItem key={`year-${year}`} value={year}>
                                        {year === 'reception'
                                            ? 'Reception'
                                            : `Year ${year}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <div className={classes.confirm}>
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={!isValid}
                            className={`${classes.button} ${classes.confirmButton}`}
                            type="submit"
                            id="submitPassenger"
                        >
                            {!isValid ? (
                                <span>&#x1F6AB;</span>
                            ) : (
                                <span>&#x2705;</span>
                            )}
                            &nbsp;Confirm
                        </Button>
                    </div>
                </div>
            </div>
        </form>
    );
};

BuyAgainForm.propTypes = {
    handleChange: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleDateChange: PropTypes.func,
    passenger: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        stop: PropTypes.string,
        dest: PropTypes.string
    }).isRequired,
    service: PropTypes.shape().isRequired,
    ticket: PropTypes.shape({
        type: PropTypes.string
    }),
    classes: PropTypes.shape().isRequired,
    requireStudentId: PropTypes.bool
};

BuyAgainForm.defaultProps = {
    ticket: null,
    handleDateChange: () => {},
    requireStudentId: false
};

export default withStyles(styles)(BuyAgainForm);
